import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { isNil } from 'lodash'
import { Dropdown } from 'react-bootstrap'
import { connect, useDispatch } from 'react-redux'

import routes, { categoriesGroup } from '../../routers'
import { logout } from '../../service/auth'
import { DROPDOWN_HEADER_RECIPES, LINK_TO_HELP_PAGE } from '../../utils/constants'
import { ArrowBack, InviteFamIcon, UserWithoutPhoto } from '../SvgIcon'
import DropDownCustom from '../DropDownCustom'
import NotificationBell from '../NotificationBell'
import RecipeSearch from '../RecipeSearch'

const MenuBtn = ({ openMenu, setOpenMenu }) => {
  return (
    <button
      type="button"
      className="btnMenu"
      aria-label={openMenu ? 'Close menu' : 'Open menu'}
      aria-expanded={openMenu}
      onClick={setOpenMenu}
    >
      <div className={`btnBurger ${openMenu ? 'active' : ''}`}>
        <span className="spanBtnBurger" />
        <span className="spanBtnBurger" />
        <span className="spanBtnBurger" />
      </div>
    </button>
  )
}

// Opened from the home screen icon: the phone shows no browser back button
const isInstalledApp = () =>
  window.navigator.standalone === true ||
  (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)

// Pages with nothing to go back to
const HOME_PAGES = [routes.signIn, routes.userProfile]

const BackButton = () => {
  const navigate = useNavigate()
  // Earlier pages in this visit go back; opened straight onto a page, go to the recipes
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate(routes.userProfile))
  return (
    <button type="button" className="mobileBackBtn" aria-label="Back" onClick={goBack}>
      <ArrowBack className="mobileBackIcon" />
    </button>
  )
}

const firstName = (fullname) => (fullname || '').trim().split(' ')[0]

const UserPhoto = ({ avatar, fullname }) => {
  return (
    <div className="innerUserHeaderInfo">
      {!isNil(avatar) ? (
        <img src={avatar} className="userAvatar" alt="" />
      ) : (
        <UserWithoutPhoto className="userAvatar" />
      )}
      {!isNil(fullname) ? <p className="userName">{firstName(fullname)}</p> : null}
    </div>
  )
}

const MobileMenu = ({ open, groups, cookbooks, recipeMenu, onSignOut }) => (
  <nav className={`innerHeaderMenu ${open ? 'openMenu' : ''}`} aria-label="Menu">
    <div className="mobileMenuSection">
      <p className="mobileMenuLabel">Recipes</p>
      {recipeMenu.map(({ title, route }) => (
        <Link key={title} to={route} className="navigateMenuItem">
          {title}
        </Link>
      ))}
    </div>
    <div className="mobileMenuSection">
      <p className="mobileMenuLabel">Your groups</p>
      {groups?.map((item) => (
        <Link
          key={item?.id}
          to={categoriesGroup(item?.slug, item?.id)}
          className="navigateMenuItem"
        >
          {item?.name}
        </Link>
      ))}
      <Link to={routes.manageFams} className="navigateMenuItem navigateMenuItemMuted">
        + Create a group
      </Link>
    </div>
    <div className="mobileMenuSection">
      {cookbooks && (
        <Link to={routes.purchaseBook} className="navigateMenuItem">
          Printed cookbooks
        </Link>
      )}
      <Link to={routes.invite} className="navigateMenuItem">
        Invite your fam
      </Link>
      <Link to={routes.settings} className="navigateMenuItem">
        Your account
      </Link>
      <a
        target="_blank"
        rel="noopener noreferrer"
        className="navigateMenuItem"
        href={LINK_TO_HELP_PAGE}
      >
        Help
      </a>
      <button type="button" className="navigateMenuItem" onClick={onSignOut}>
        Sign out
      </button>
    </div>
  </nav>
)

const Header = ({ user, groups }) => {
  const [openMenu, setOpenMenu] = useState(false)
  const dispatch = useDispatch()
  const navigation = useNavigate()
  const location = useLocation()
  const cookbooks = !!user?.cookbook_enabled
  const [installed] = useState(isInstalledApp)
  const showBack = installed && !HOME_PAGES.includes(location.pathname)

  // Close the phone menu whenever the page changes
  useEffect(() => setOpenMenu(false), [location.pathname])

  const signOut = () => logout(navigation)(dispatch)

  // Recipes menu, plus "Ask for a recipe" once recipe request links are on for them
  const recipeMenu = [
    ...DROPDOWN_HEADER_RECIPES,
    ...(user?.features?.bulk_scan ? [{ title: 'Scan a recipe box', route: routes.scanBox }] : []),
    ...(user?.features?.recipe_requests
      ? [{ title: 'Ask for a recipe', route: routes.recipeRequests }]
      : []),
  ]

  const NAVIGATION_HEADER_PROFILE = [
    { name: 'Your account', onClick: () => navigation(routes.settings) },
    ...(user?.features?.gifts
      ? [{ name: 'Give a gift', onClick: () => navigation(routes.gift) }]
      : []),
    ...(cookbooks
      ? [{ name: 'Cookbook orders', onClick: () => navigation(routes.cookbookOrders) }]
      : []),
    { name: 'Help', onClick: () => window.open(LINK_TO_HELP_PAGE, '_blank', 'noopener') },
    { name: 'Sign out', onClick: signOut },
  ]

  return (
    <div className="innerHeaderComponent">
      <header className="header">
        <div className="headerInner headerContainer">
          <div className="headerItemFirst">
            <Link className="innerLogoSite" to={routes.userProfile}>
              <img src="/logo.svg" alt="Morsel" className="headerLogo" />
            </Link>
            <nav className="headerNav" aria-label="Main">
              <DropDownCustom link="Recipes" globalClassName="dropTheme headerNavItem">
                {recipeMenu.map(({ title, route }) => (
                  <Link key={title} to={route} className="groupsItem headerMenuItem">
                    {title}
                  </Link>
                ))}
              </DropDownCustom>
              <DropDownCustom
                link="Groups"
                globalClassName="dropTheme headerNavItem"
                styleToggleList="headerMenuScroll"
              >
                {groups?.length ? <p className="headerMenuLabel">Your groups</p> : null}
                {groups?.map((item) => (
                  <Dropdown.Item
                    key={item?.id}
                    onClick={() => navigation(categoriesGroup(item?.slug, item?.id))}
                    className="groupsItem headerMenuItem"
                  >
                    {item?.name}
                  </Dropdown.Item>
                ))}
                <Link to={routes.manageFams} className="groupsItem headerMenuItem headerMenuAdd">
                  + Create a group
                </Link>
              </DropDownCustom>
              {cookbooks && (
                <NavLink to={routes.purchaseBook} className="headerNavLink">
                  Cookbooks
                  <span className="headerNavBadge">New</span>
                </NavLink>
              )}
            </nav>
          </div>
          <div className="headerItemSecond">
            <Link to={routes.invite} className="inviteBtn">
              <InviteFamIcon /> Invite your fam
            </Link>
            <RecipeSearch className="headerRecipeSearch" />
            <NotificationBell className="headerNotificationBell" />
            <DropDownCustom
              globalClassName="dropTheme headerUserMenu"
              link={<UserPhoto avatar={user?.avatar} fullname={user?.fullname} />}
            >
              {NAVIGATION_HEADER_PROFILE?.map(({ name, onClick }) => (
                <Dropdown.Item key={name} onClick={onClick} className="groupsItem headerMenuItem">
                  {name}
                </Dropdown.Item>
              ))}
            </DropDownCustom>
          </div>
        </div>
        <div className="headerContainer mobileHeader">
          <div className="innerMobileHeader">
            <div className="mobileHeaderStart">
              {showBack ? <BackButton /> : null}
              <Link className="innerLogoSite" to={routes.userProfile}>
                <img src="/logo.svg" alt="Morsel" className="headerLogo" />
              </Link>
            </div>
            <div className="mobileHeaderActions">
              <RecipeSearch />
              <NotificationBell />
              <Link to={routes.settings} aria-label="Your account">
                <UserPhoto avatar={user?.avatar} />
              </Link>
              <MenuBtn
                openMenu={openMenu}
                setOpenMenu={() => setOpenMenu((oldState) => !oldState)}
              />
            </div>
          </div>
        </div>
      </header>
      <MobileMenu
        open={openMenu}
        groups={groups}
        cookbooks={cookbooks}
        recipeMenu={recipeMenu}
        onSignOut={signOut}
      />
    </div>
  )
}

export default connect(({ user, groups }) => ({ user: user.data, groups: groups.data }))(Header)
